import {
  Card,
  Icon,
  Media,
  MediaWithViewer,
  Space,
  SubText,
  WithPopover,
} from '@procurify/ui'
import { useSelector } from 'react-redux'

import {
  DataTable,
  configureColumn as col,
  type RowCellMenuListItemProps,
} from '@webapp/components/DataTable'
import { CreditCardLabel } from '@webapp/core/components/CreditCardLabel'
import { useFormatMedia } from '@webapp/core/hooks/useFormatMedia'
import { makeSelectFeatureSwitches } from '@webapp/core/stores/global'
import {
  useFeatureFlag,
  useFormatTaxPercentage,
  useProcurifyIntl,
} from '@webapp/hooks'
import { type Enum } from '@webapp/infrastructure/types/Enum'
import { ExpenseTypeSummary } from './components/ExpenseTypeSummary/ExpenseTypeSummary'
import messages from './messages'
import { NotesRowStyled } from './styles/NotesRowStyled'
import { ExpensePaymentTypes } from '../../entities/enums/ExpensePaymentTypeEnum'
import { ExpenseTypes } from '../../entities/enums/ExpenseTypeEnum'
import { type ExpenseItem } from '../../entities/ExpenseItem'
import { ExpenseTypeLabel } from '../ExpenseTypeLabel'

export const ExpenseViewerRoles = {
  REQUESTER: 'REQUESTER',
  APPROVER: 'APPROVER',
} as const
export type ExpenseViewerRoleEnum = Enum<typeof ExpenseViewerRoles>

export interface IExpenseItemTableProps {
  items: ExpenseItem[]
  isLoading?: boolean
  loaderRows?: number
  menuListItems?: (
    data: ExpenseItem,
    rowIndex: number
  ) => RowCellMenuListItemProps[]
  onAttachmentClick?: (item: ExpenseItem, itemIndex: number) => void
  viewerRole?: ExpenseViewerRoleEnum
}

export const ExpenseItemTable = ({
  items,
  isLoading = false,
  loaderRows,
  menuListItems,
  onAttachmentClick,
  viewerRole = ExpenseViewerRoles.APPROVER,
}: IExpenseItemTableProps) => {
  const intl = useProcurifyIntl()
  const formatMedia = useFormatMedia()
  const formatTaxPercentage = useFormatTaxPercentage()
  const PAY_3067_ENHANCE_MILEAGE_EXPENSE_ITEMS = useFeatureFlag(
    'PAY_3067_ENHANCE_MILEAGE_EXPENSE_ITEMS'
  )
  const { reclaimable_expense_mileage_tax } = useSelector(
    makeSelectFeatureSwitches()
  )

  const hasActiveTax = (data: ExpenseItem) =>
    data.taxId != null ||
    data.taxName === '$' ||
    (data.taxName === '%' && Number.parseFloat(data.taxPercentage) > 0)

  const shouldHideMileageTax = (data: ExpenseItem) =>
    viewerRole === ExpenseViewerRoles.REQUESTER &&
    reclaimable_expense_mileage_tax &&
    data.expenseType === ExpenseTypes.MILEAGE

  return (
    <DataTable<ExpenseItem>
      id='expense-item-table'
      data={items}
      isLoading={isLoading}
      loaderRows={loaderRows}
      aria-label={intl.formatMessage(messages.tableLabel)}
      emptyStateText={intl.formatMessage(messages.emptyStateText)}
      customizableColumns={false}
      menuListItems={menuListItems}
      renderAdditionalRows={(item) => {
        if (!item.hasNotes) return []

        return [
          [
            {
              colSpan: 99,
              render: (
                <NotesRowStyled>
                  <Space direction='horizontal'>
                    <Space direction='horizontal' size='small'>
                      <Icon icon='message' size={18} />
                      <SubText bold>
                        {intl.formatMessage(messages.notesPrefix)}
                      </SubText>
                    </Space>
                    <SubText breakWord>{item.notes}</SubText>
                  </Space>
                </NotesRowStyled>
              ),
            },
          ],
        ]
      }}
      columns={[
        col('BASIC', {
          width: 1,
          label: '',
          locked: true,
          getter: (data, rowIndex) => {
            return rowIndex + 1
          },
          loaderWidth: 10,
        }),
        col('BASIC', {
          label: intl.formatMessage(messages.receiptLabel),
          width: 1,
          loaderWidth: 10,
          getter: (data, index) => {
            if (!data.attachment) return null

            return onAttachmentClick ? (
              <Media
                src={formatMedia(data.attachment)}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  onAttachmentClick(data, index)
                }}
              />
            ) : (
              <MediaWithViewer src={formatMedia(data.attachment)} />
            )
          },
        }),
        col('BASIC', {
          label: intl.formatMessage(messages.vendorNameLabel),
          getter: (data) => {
            return data.merchant
          },
        }),
        col('BUDGET_ACCOUNTCODE', {
          label: intl.formatMessage(messages.accountCodeLabel),
          getter: (data) => {
            return {
              budget: data.account
                ? {
                    accountCode: data.account.accountCode.code,
                    description: data.account.accountCode.description,
                  }
                : null,
              hasMultiple: false,
            }
          },
        }),
        col('BASIC', {
          label: intl.formatMessage(messages.expenseTypeLabel),
          getter: (data) => {
            return (
              <WithPopover
                content={
                  <Card.Content>
                    <ExpenseTypeSummary item={data} />
                  </Card.Content>
                }
              >
                <ExpenseTypeLabel type={data.expenseType} />
              </WithPopover>
            )
          },
        }),
        col('DATE', {
          width: 0,
          label: intl.formatMessage(messages.dateLabel),
          getter: (data) => {
            return {
              date: data.transactionDate || null,
            }
          },
        }),
        col('BASIC', {
          label: intl.formatMessage(messages.paymentLabel),
          getter: (data) => {
            if (data.paymentCard) {
              return (
                <CreditCardLabel
                  type={data.paymentCard.vendor}
                  last4Digits={data.paymentCard.last4Digits}
                  name={data.paymentCard.name}
                />
              )
            }
            const paymentTypeMessageMap: Partial<
              Record<number, (typeof messages)[keyof typeof messages]>
            > = {
              [ExpensePaymentTypes.CASH]: messages.paymentTypeCash,
              [ExpensePaymentTypes.CHEQUE]: messages.paymentTypeCheque,
              [ExpensePaymentTypes.OTHER]: messages.paymentTypeOther,
            }
            const msg = paymentTypeMessageMap[data.paymentType]
            return msg ? intl.formatMessage(msg) : '--'
          },
        }),
        col('BASIC', {
          label: intl.formatMessage(messages.reimbursableLabel),
          width: 1,
          getter: (data) => {
            return data.reimbursable
              ? intl.formatMessage(messages.positiveReimburseValue)
              : intl.formatMessage(messages.negativeReimburseValue)
          },
        }),
        ...(PAY_3067_ENHANCE_MILEAGE_EXPENSE_ITEMS
          ? [
              col('BASIC', {
                label: intl.formatMessage(messages.taxLabel),
                getter: (data: ExpenseItem) => {
                  if (shouldHideMileageTax(data)) {
                    return '--'
                  }

                  if (data.taxId != null) {
                    return intl.formatMessage(messages.taxProfileValue, {
                      name: data.taxName,
                      percentage: formatTaxPercentage(data.taxPercentage, 0),
                    })
                  }

                  // Custom tax amount
                  if (data.taxName === '$') {
                    return intl.formatMessage(messages.taxCustomAmountValue)
                  }

                  // Custom tax percentage
                  if (
                    data.taxName === '%' &&
                    Number.parseFloat(data.taxPercentage) > 0
                  ) {
                    return formatTaxPercentage(data.taxPercentage, 0)
                  }

                  return '--'
                },
              }),
            ]
          : []),
        col('PRICE', {
          label: intl.formatMessage(
            PAY_3067_ENHANCE_MILEAGE_EXPENSE_ITEMS
              ? messages.taxAmountLabel
              : messages.taxLabel
          ),
          getter: (data) => {
            if (shouldHideMileageTax(data)) {
              return {
                price: 0,
                currencyName: data.currency.name,
                renderZeroPrice: false,
              }
            }
            return {
              price: data.taxAmount,
              currencyName: data.currency.name,
              renderZeroPrice: hasActiveTax(data),
            }
          },
        }),
        col('PRICE', {
          label: intl.formatMessage(messages.totalAmountLabel),
          getter: (data) => {
            return {
              price: data.amount,
              currency: data.currency,
              renderZeroPrice: true,
            }
          },
        }),
      ]}
    />
  )
}
